import React, { useEffect, useState } from 'react'
import { TextField, Stack } from '@mui/material'
import { postUserPhoneCode, postUserPhoneChange, getSecurityOptions } from '@/api/user'
import Modal2 from '@/components/Modal2'
import { Controller, useForm } from 'react-hook-form'
import useCountDown from '@/hooks/useCountDown'
import { AuthCodeInput, VerifyRes } from './common'
import { Message } from '@cx/ui'
import { useRequest } from 'ahooks'
import SecurityVerifyPanel from './SecurityVerifyPanel'

interface ModifyPhoneModalProps {
  open: boolean
  onCancel: () => void
  onOk: () => void
  currentPhone?: string
  currentMail?: string
}

const ModifyPhoneModal: React.FC<ModifyPhoneModalProps> = ({ open, onCancel, onOk, currentPhone, currentMail }) => {
  const [verifyType, setVerifyType] = useState('')
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    trigger,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      verify_code: '',
      verify_password: '',
      verify_state: '',
      new_phone: '',
      new_code: '',
    },
  })

  const [newCountDown, startNew] = useCountDown('MODIFY_PHONE_NEW_COUNT_DOWN')

  const { data: securityOptions, runAsync: loadSecurityOptions } = useRequest(() => getSecurityOptions('change_phone'), {
    manual: true,
  })

  useEffect(() => {
    if (open) {
      loadSecurityOptions().then((res) => {
        setVerifyType(res?.recommended_verify_type || res?.available_verify_types?.[0] || '')
      })
    } else {
      reset()
      setVerifyType('')
    }
  }, [open, loadSecurityOptions, reset])

  const getNewPhoneCode = async (verify: (callback: (p: VerifyRes) => Promise<void>) => void) => {
    if (newCountDown > 0) return
    trigger('new_phone').then((res) => {
      if (res) {
        const requestCode = (p: VerifyRes) =>
          postUserPhoneCode({ phone: getValues('new_phone'), kind: 3, ...p }).then(() => {
            startNew()
          })

        verify(requestCode)
      }
    })
  }

  const onSubmit = handleSubmit((data) => {
    postUserPhoneChange({
      verify_type: verifyType as 'phone' | 'email' | 'wechat',
      verify_code: data.verify_code,
      verify_password: data.verify_password,
      verify_state: data.verify_state,
      new_phone: data.new_phone,
      new_code: data.new_code,
    }).then(() => {
      onOk()
      onCancel()
      Message.success('修改成功')
    })
  })

  return (
    <Modal2 title='修改手机' width={625} open={open} onCancel={onCancel} onOk={onSubmit}>
      <Stack gap={3}>
        <SecurityVerifyPanel
          scene='change_phone'
          open={open}
          options={securityOptions}
          verifyType={verifyType}
          onVerifyTypeChange={setVerifyType}
          control={control}
          errors={errors}
          trigger={trigger}
          setValue={setValue}
          watch={watch}
          currentPhone={currentPhone}
          currentMail={currentMail}
        />
        <Controller
          rules={{
            required: '请输入新手机号',
            validate: (value) => /^1[3-9]\d{9}$/.test(value) || '手机号格式错误',
          }}
          name='new_phone'
          control={control}
          render={({ field }) => (
            <TextField
              required
              {...field}
              variant='outlined'
              label='新手机号'
              fullWidth
              error={Boolean(errors.new_phone)}
              helperText={errors.new_phone?.message as string}
              size='small'
              autoComplete='off'
            />
          )}
        />
        <Controller
          rules={{ required: '请输入验证码' }}
          name='new_code'
          control={control}
          render={({ field }) => (
            <AuthCodeInput
              required
              {...field}
              variant='outlined'
              label='新手机号验证码'
              placeholder='请输入验证码'
              fullWidth
              error={Boolean(errors.new_code)}
              helperText={errors.new_code?.message as string}
              size='small'
              autoComplete='off'
              countDown={newCountDown}
              onVerify={() => {
                return trigger('new_phone')
              }}
              getPhoneCode={getNewPhoneCode}
            />
          )}
        />
      </Stack>
    </Modal2>
  )
}

export default ModifyPhoneModal
